import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Box, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Typography, Button, TextField, Dialog, DialogTitle, DialogContent, DialogActions,
  FormControl, InputLabel, Select, MenuItem, IconButton,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import Layout from '../../components/layout/Layout';
import {
  getMenuMasterItems, addMenuMasterItem, updateMenuMasterItem, deleteMenuMasterItem,
  menuMasterFlagOff, seedMenuMaster,
} from '../../Redux/Actions/menuMasterAction';
import toast from 'react-hot-toast';

const CATEGORIES = [
  { id: 'espresso', label: '에스프레소' },
  { id: 'brewed_coffee', label: '브루커피' },
  { id: 'tea', label: '티' },
  { id: 'chocolate', label: '초코라테' },
  { id: 'cold_drink', label: '콜드 드링크' },
];
const SIZE_LABELS = { hot_s: 'HOT S', hot_m: 'HOT M', hot_l: 'HOT L', ice_s: 'ICED S', ice_m: 'ICED M', ice_l: 'ICED L' };

const emptyItem = { category: 'espresso', code: '', name: '', nameEn: '', order: 0, calories: {} };

function MenuMaster({ user }) {
  const dispatch = useDispatch();
  const { items, loading, isAdded, isUpdated, isDeleted } = useSelector(s => s.MenuMaster);
  const [categoryFilter, setCategoryFilter] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editItem, setEditItem] = useState(null);
  const [form, setForm] = useState(emptyItem);
  const [deleteId, setDeleteId] = useState(null);

  useEffect(() => { dispatch(getMenuMasterItems()); }, []);
  useEffect(() => {
    if (isAdded) { toast.success('추가되었습니다.'); dispatch(menuMasterFlagOff()); setDialogOpen(false); setEditItem(null); }
    if (isUpdated) { toast.success('수정되었습니다.'); dispatch(menuMasterFlagOff()); setDialogOpen(false); setEditItem(null); }
    if (isDeleted) { toast.success('삭제되었습니다.'); dispatch(menuMasterFlagOff()); setDeleteId(null); }
  }, [isAdded, isUpdated, isDeleted]);

  const filtered = categoryFilter
    ? items.filter(i => i.category === categoryFilter)
    : items;

  const openAdd = () => {
    setEditItem(null);
    setForm({ ...emptyItem, category: categoryFilter || 'espresso' });
    setDialogOpen(true);
  };
  const openEdit = (item) => {
    setEditItem(item);
    setForm({
      category: item.category,
      code: item.code,
      name: item.name,
      nameEn: item.nameEn || '',
      order: item.order || 0,
      calories: { ...(item.calories || {}) },
    });
    setDialogOpen(true);
  };
  const handleSave = () => {
    if (!form.code?.trim() || !form.name?.trim()) {
      toast.error('코드와 이름을 입력하세요.');
      return;
    }
    const cal = {};
    ['hot_s', 'hot_m', 'hot_l', 'ice_s', 'ice_m', 'ice_l'].forEach(k => {
      const v = form.calories[k];
      if (v !== undefined && v !== '') cal[k] = Number(v) || 0;
    });
    if (editItem) {
      dispatch(updateMenuMasterItem(editItem._id, { ...form, calories: cal }));
    } else {
      dispatch(addMenuMasterItem({ ...form, calories: cal }));
    }
  };
  const handleSeed = () => {
    dispatch(seedMenuMaster());
    toast.success('기본 템플릿을 불러왔습니다.');
  };

  return (
    <Layout title="메뉴 마스터 (메뉴보드 템플릿)" user={user}>
      <Box sx={{ p: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2, flexWrap: 'wrap' }}>
          <FormControl size="small" sx={{ minWidth: 160 }}>
            <InputLabel>카테고리</InputLabel>
            <Select
              label="카테고리"
              value={categoryFilter}
              onChange={e => setCategoryFilter(e.target.value)}
            >
              <MenuItem value="">전체</MenuItem>
              {CATEGORIES.map(c => <MenuItem key={c.id} value={c.id}>{c.label}</MenuItem>)}
            </Select>
          </FormControl>
          <Button variant="outlined" onClick={handleSeed} sx={{ borderRadius: 2 }}>
            기본 템플릿 불러오기
          </Button>
          <Button variant="contained" startIcon={<AddIcon />} onClick={openAdd} sx={{ background: '#551FFF', borderRadius: 2 }}>
            메뉴 추가
          </Button>
        </Box>

        <TableContainer component={Paper} sx={{ borderRadius: 3, boxShadow: '0 2px 8px rgba(0,0,0,0.06)', overflowX: 'auto' }}>
          <Table size="small">
            <TableHead>
              <TableRow sx={{ background: '#f5f3ff' }}>
                <TableCell sx={{ fontWeight: 700 }}>카테고리</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>코드</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>이름</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>칼로리 (HOT S/M/L, ICED S/M/L)</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>관리</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading && (
                <TableRow><TableCell colSpan={5} align="center">로딩 중...</TableCell></TableRow>
              )}
              {!loading && filtered.length === 0 && (
                <TableRow><TableCell colSpan={5} align="center" sx={{ py: 4, color: '#888' }}>메뉴가 없습니다. 기본 템플릿 불러오기를 눌러 주세요.</TableCell></TableRow>
              )}
              {!loading && filtered.map(row => (
                <TableRow key={row._id} hover>
                  <TableCell>{CATEGORIES.find(c => c.id === row.category)?.label || row.category}</TableCell>
                  <TableCell>{row.code}</TableCell>
                  <TableCell>{row.name} {row.nameEn && <span style={{ color: '#888', fontSize: 12 }}>({row.nameEn})</span>}</TableCell>
                  <TableCell sx={{ fontSize: 12 }}>
                    {['hot_s', 'hot_m', 'hot_l', 'ice_s', 'ice_m', 'ice_l'].map(k => (
                      <span key={k} style={{ marginRight: 8 }}>{SIZE_LABELS[k]}: {(row.calories || {})[k] ?? '-'}</span>
                    ))}
                  </TableCell>
                  <TableCell>
                    <IconButton size="small" onClick={() => openEdit(row)}><EditIcon fontSize="small" /></IconButton>
                    <IconButton size="small" onClick={() => setDeleteId(row._id)} sx={{ color: '#e53935' }}><DeleteOutlineIcon fontSize="small" /></IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>

        {deleteId && (
          <Dialog open onClose={() => setDeleteId(null)}>
            <DialogTitle>삭제</DialogTitle>
            <DialogContent>이 메뉴 항목을 삭제하시겠습니까?</DialogContent>
            <DialogActions>
              <Button onClick={() => setDeleteId(null)}>취소</Button>
              <Button color="error" variant="contained" onClick={() => { dispatch(deleteMenuMasterItem(deleteId)); setDeleteId(null); }}>삭제</Button>
            </DialogActions>
          </Dialog>
        )}

        <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth>
          <DialogTitle>{editItem ? '메뉴 수정' : '메뉴 추가'}</DialogTitle>
          <DialogContent>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
              <FormControl size="small" fullWidth>
                <InputLabel>카테고리</InputLabel>
                <Select label="카테고리" value={form.category} onChange={e => setForm(f => ({ ...f, category: e.target.value }))}>
                  {CATEGORIES.map(c => <MenuItem key={c.id} value={c.id}>{c.label}</MenuItem>)}
                </Select>
              </FormControl>
              <TextField size="small" label="코드" value={form.code} onChange={e => setForm(f => ({ ...f, code: e.target.value }))} placeholder="americano" disabled={!!editItem} />
              <TextField size="small" label="이름" value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} placeholder="아메리카노" />
              <TextField size="small" label="영문명" value={form.nameEn} onChange={e => setForm(f => ({ ...f, nameEn: e.target.value }))} placeholder="Americano" />
              <Typography variant="subtitle2" sx={{ color: '#551FFF', mt: 1 }}>칼로리 (사이즈별)</Typography>
              <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 1 }}>
                {['hot_s', 'hot_m', 'hot_l', 'ice_s', 'ice_m', 'ice_l'].map(k => (
                  <TextField
                    key={k}
                    size="small"
                    label={SIZE_LABELS[k]}
                    type="number"
                    value={form.calories[k] ?? ''}
                    onChange={e => setForm(f => ({ ...f, calories: { ...f.calories, [k]: e.target.value } }))}
                  />
                ))}
              </Box>
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setDialogOpen(false)}>취소</Button>
            <Button variant="contained" onClick={handleSave} sx={{ background: '#551FFF' }}>저장</Button>
          </DialogActions>
        </Dialog>
      </Box>
    </Layout>
  );
}

export default MenuMaster;
